import { useRef, useState } from "react";
import { Plus, Trash2, Upload } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PieChart } from "../../charts/PieChart";
import { TrendChart } from "../../charts/TrendChart";
import { assetTypeColor } from "../../charts/colors";
import { assetTypeLabels, formatSEK0 } from "../../lib/format";
import type { InvestmentsOverview } from "../../types";

interface InvestmentsSectionProps {
  data: InvestmentsOverview | null;
  onRefresh: () => void;
}

export function InvestmentsSection({ data, onRefresh }: InvestmentsSectionProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addName, setAddName] = useState("");
  const [addValue, setAddValue] = useState("");
  const [addAccount, setAddAccount] = useState("");
  const [addCost, setAddCost] = useState("");

  async function handleImport(file: File) {
    setImporting(true);
    setImportError(null);
    setImportMessage(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch("/api/investments/import", {
        method: "POST",
        body: formData,
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error ?? "Importen misslyckades");
      setImportMessage(
        `${result.imported} positioner importerade${
          result.importMethod === "ai" ? " (via AI-extraktion)" : ""
        }.`
      );
      onRefresh();
    } catch (err: any) {
      setImportError(err.message);
    } finally {
      setImporting(false);
    }
  }

  async function handleAdd() {
    const value = Number(addValue.replace(",", "."));
    if (!addName.trim() || !Number.isFinite(value) || value <= 0) return;
    await fetch("/api/investments", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: addName.trim(),
        value,
        account: addAccount.trim() || undefined,
        costBasis: addCost ? Number(addCost.replace(",", ".")) : undefined,
      }),
    });
    setAddName("");
    setAddValue("");
    setAddAccount("");
    setAddCost("");
    setShowAddForm(false);
    onRefresh();
  }

  async function handleDelete(id: string) {
    await fetch(`/api/investments/${id}`, { method: "DELETE" });
    onRefresh();
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const delta = data.totalCostBasis > 0 ? data.totalValue - data.totalCostBasis : null;
  const deltaPct =
    delta !== null && data.totalCostBasis > 0
      ? (delta / data.totalCostBasis) * 100
      : null;

  const byType = new Map<string, number>();
  for (const holding of data.holdings) {
    byType.set(holding.assetType, (byType.get(holding.assetType) ?? 0) + holding.value);
  }
  const allocation = [...byType.entries()]
    .map(([type, value]) => ({
      label: assetTypeLabels[type] ?? type,
      value: Math.round(value),
      color: assetTypeColor(type),
      key: type,
    }))
    .sort((a, b) => b.value - a.value);

  return (
    <div className="space-y-4 lg:space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Totalt värde</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold tabular-nums">{formatSEK0(data.totalValue)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Utveckling</CardTitle>
          </CardHeader>
          <CardContent>
            {delta !== null ? (
              <p
                className={`text-2xl font-bold tabular-nums ${delta >= 0 ? "text-success" : "text-destructive"}`}
              >
                {delta >= 0 ? "+" : ""}
                {formatSEK0(delta)}
                {deltaPct !== null && (
                  <span className="ml-1 text-sm font-medium">
                    ({deltaPct >= 0 ? "+" : ""}
                    {deltaPct.toFixed(1)}%)
                  </span>
                )}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">Anskaffningsvärde saknas</p>
            )}
          </CardContent>
        </Card>
        <Card className="col-span-2 lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Innehav</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold tabular-nums">{data.holdings.length}</p>
            {data.accounts.length > 0 && (
              <p className="mt-1 truncate text-xs text-muted-foreground">
                {data.accounts.join(" · ")}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Importera positioner</CardTitle>
          <CardDescription>
            Exportera dina positioner från Avanza/Nordnet (CSV eller Excel) och släpp
            filen här — värde, GAV och konto läses in automatiskt. PDF:er och
            skärmdumpar tolkas med AI.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
            >
              <Upload className="mr-2 h-4 w-4" />
              {importing ? "Importerar..." : "Välj positionsfil"}
            </Button>
            <Button variant="outline" onClick={() => setShowAddForm((v) => !v)}>
              <Plus className="mr-2 h-4 w-4" /> Lägg till manuellt
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleImport(file);
                e.target.value = "";
              }}
            />
          </div>

          {showAddForm && (
            <div className="grid gap-2 rounded-lg border p-3 sm:grid-cols-2 lg:grid-cols-4">
              <Input
                placeholder="Namn (t.ex. Investor B)"
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
              />
              <Input
                placeholder="Värde (kr)"
                inputMode="decimal"
                value={addValue}
                onChange={(e) => setAddValue(e.target.value)}
              />
              <Input
                placeholder="Konto (valfritt)"
                value={addAccount}
                onChange={(e) => setAddAccount(e.target.value)}
              />
              <div className="flex gap-2">
                <Input
                  placeholder="Anskaffningsvärde"
                  inputMode="decimal"
                  value={addCost}
                  onChange={(e) => setAddCost(e.target.value)}
                />
                <Button onClick={() => void handleAdd()} disabled={!addName.trim() || !addValue}>
                  Spara
                </Button>
              </div>
            </div>
          )}

          {importMessage && (
            <div className="rounded-md bg-success/10 px-4 py-2.5 text-sm text-success">
              {importMessage}
            </div>
          )}
          {importError && (
            <div className="rounded-md bg-destructive/10 px-4 py-2.5 text-sm text-destructive">
              {importError}
            </div>
          )}
        </CardContent>
      </Card>

      {data.holdings.length > 0 && (
        <div className="grid gap-4 lg:grid-cols-2 lg:gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Fördelning</CardTitle>
            </CardHeader>
            <CardContent>
              <PieChart data={allocation} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Portföljvärde över tid</CardTitle>
            </CardHeader>
            <CardContent>
              <TrendChart
                data={data.history.map((s) => ({ date: s.date, value: s.value }))}
                height={220}
              />
            </CardContent>
          </Card>
        </div>
      )}

      {data.holdings.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Innehav</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 font-medium text-muted-foreground">Namn</th>
                    <th className="pb-2 text-right font-medium text-muted-foreground">Antal</th>
                    <th className="pb-2 text-right font-medium text-muted-foreground">Värde</th>
                    <th className="pb-2 text-right font-medium text-muted-foreground">+/−</th>
                    <th className="w-8 pb-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.holdings.map((holding) => {
                    const pl =
                      holding.costBasis && holding.costBasis > 0
                        ? holding.value - holding.costBasis
                        : null;
                    return (
                      <tr key={holding.id} className="group border-b border-border/50">
                        <td className="max-w-[240px] py-2.5 pr-3">
                          <div className="truncate font-medium" title={holding.name}>
                            {holding.name}
                          </div>
                          <div className="truncate text-xs text-muted-foreground">
                            {[assetTypeLabels[holding.assetType], holding.account]
                              .filter(Boolean)
                              .join(" · ")}
                          </div>
                        </td>
                        <td className="py-2.5 pr-3 text-right tabular-nums text-muted-foreground">
                          {holding.quantity > 0 ? holding.quantity.toLocaleString("sv-SE") : "—"}
                        </td>
                        <td className="py-2.5 pr-3 text-right font-medium tabular-nums whitespace-nowrap">
                          {formatSEK0(holding.value)}
                        </td>
                        <td
                          className={`py-2.5 pr-1 text-right tabular-nums whitespace-nowrap ${
                            pl === null
                              ? "text-muted-foreground"
                              : pl >= 0
                                ? "text-success"
                                : "text-destructive"
                          }`}
                        >
                          {pl === null ? "—" : `${pl >= 0 ? "+" : ""}${formatSEK0(pl)}`}
                        </td>
                        <td className="py-2.5">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100 max-lg:opacity-60"
                            onClick={() => void handleDelete(holding.id)}
                            aria-label={`Ta bort ${holding.name}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
